import { Minus, PauseIcon, PlayIcon, PlusIcon, Trash2, User2Icon, Upload } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import Papa from 'papaparse';
import Input from '../../components/Input';
import Modal from '../../components/Modal';
import Toggle from '../../components/Toggle';
import APIUrls from '../../core/ApiUrls';
import Toastify from '../../core/Toastify';
import { formatDate, MakeAxiosRequest } from '../../core/utils';

export interface ReporterLead {
    id: string;
    user_id: string;

    linkedin_url: string;

    full_name: string | null;
    profile_image_url: string | null;
    headline: string | null;
    location: string | null;

    last_job_title: string | null;
    last_company_name: string | null;
    last_company_id: string | null;

    last_experience: any | null;
    last_education: any | null;

    last_profile_hash: string | null;

    last_company_domain: string | null;
    last_company_size: string | null;
    last_company_industry: string | null;

    last_fetched_at: string | null; // ISO timestamp
    created_at: string;
    updated_at: string;

    status: {
        isRunning?: boolean;
        isPaused?: boolean;
        workflowId?: string;
        runId?: string;
        status?: string;
    };
}

const Leads = () => {
    const [linkedinUrls, setLinkedinUrls] = useState<string[]>(['']);
    const [leads, setLeads] = useState<ReporterLead[]>([]);
    const [uploadLeadsModal, setUploadLeadsModal] = useState<boolean>(false);
    const [deleteLeadId, setDeleteLeadId] = useState<string>();
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const csvFileInputRef = useRef<HTMLInputElement>(null);

    // Toggle states for lead configuration
    const [alertJobChange, setAlertJobChange] = useState<boolean>(true);
    const [alertPosts, setAlertPosts] = useState<boolean>(true);
    const [alertExperienceChange, setAlertExperienceChange] = useState<boolean>(false);
    const [alertTitleChange, setAlertTitleChange] = useState<boolean>(true);
    const [alertHiringOrFiring, setAlertHiringOrFiring] = useState<boolean>(true);
    const [alertMerger, setAlertMerger] = useState<boolean>(false);
    const [alertAquisition, setAlertAquisition] = useState<boolean>(false);
    const [alertFunding, setAlertFunding] = useState<boolean>(false);

    // eslint-disable-next-line
    const linkedinUrlRegex = `^https?:\/\/(www\.)?linkedin\.com\/in\/[a-zA-Z0-9-_%]+\/?$`;

    const handleCsvUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (!file) return;

        Papa.parse(file, {
            header: true,
            skipEmptyLines: true,
            complete: results => {
                const data = results.data as any[];

                // Check if linkedin_url column exists
                if (!data.length || !data[0].linkedin_url) {
                    Toastify.error('CSV must have a "linkedin_url" column');
                    if (csvFileInputRef.current) csvFileInputRef.current.value = '';
                    return;
                }

                // Extract URLs and validate
                const urls = data.map(row => (row.linkedin_url || '').trim()).filter(url => url.length > 0);

                if (urls.length === 0) {
                    Toastify.error('No valid URLs found in CSV');
                    if (csvFileInputRef.current) csvFileInputRef.current.value = '';
                    return;
                }

                const invalidUrls = urls.filter(url => !url.match(linkedinUrlRegex));
                if (invalidUrls.length > 0) {
                    Toastify.error(`Found ${invalidUrls.length} invalid LinkedIn URLs`);
                    if (csvFileInputRef.current) csvFileInputRef.current.value = '';
                    return;
                }

                if (urls.length > 10) {
                    Toastify.error(`Maximum 10 leads allowed. Found ${urls.length} in CSV`);
                    if (csvFileInputRef.current) csvFileInputRef.current.value = '';
                    return;
                }

                setLinkedinUrls(urls);
                Toastify.success(`Loaded ${urls.length} leads from CSV`);
                if (csvFileInputRef.current) csvFileInputRef.current.value = '';
            },
            error: error => {
                Toastify.error(`Error parsing CSV: ${error.message}`);
                if (csvFileInputRef.current) csvFileInputRef.current.value = '';
            },
        });
    };

    const handleSubmit = async () => {
        const $linkedInUrls = linkedinUrls.map(it => it.trim()).filter(it => it !== '');
        const errored = $linkedInUrls.find(it => !it.match(linkedinUrlRegex));
        if (errored) {
            Toastify.error('Invalid Linkedin Urls');
            return;
        }
        const reqBody = {
            command: 'UPLOAD',
            linkedin_urls: $linkedInUrls,
        };
        try {
            const res = await MakeAxiosRequest('POST', APIUrls.getURL('LEADS'), reqBody);
            if (res?.status === 200) {
                Toastify.success('Uploaded Successfully');
                setUploadLeadsModal(false);
                setLinkedinUrls(['']);
            }
        } catch (error) {
            console.log(error);
        } finally {
            fetchData();
        }
    };

    const handleStart = async (leadId: string) => {
        const reqBody = {
            leadId: leadId,
        };
        try {
            const res = await MakeAxiosRequest('POST', APIUrls.getURL('MONITOR'), reqBody);
            if (res?.status === 200) {
                Toastify.success('Lead Started');
                fetchData();
            }
        } catch (error) {
            console.log(error);
        }
    };

    const handlePause = async (leadId: string) => {
        const reqBody = {
            command: 'PAUSE',
            leadId: leadId,
        };
        try {
            const res = await MakeAxiosRequest('POST', APIUrls.getURL('LEADS'), reqBody);
            if (res?.status === 200) {
                Toastify.success('Lead Paused');
                fetchData();
            }
        } catch (error) {
            console.log(error);
        }
    };
    const handleDelete = async (leadId: string) => {
        if (!leadId) {
            Toastify.error('Error Deleting This Lead');
            return;
        }
        const reqBody = {
            command: 'DELETE',
            leadId: leadId,
        };
        try {
            const res = await MakeAxiosRequest('POST', APIUrls.getURL('LEADS'), reqBody);
            if (res?.status === 200) {
                Toastify.success('Lead Deleted');
                setDeleteLeadId(undefined);
                fetchData();
            }
        } catch (error) {
            console.log(error);
        }
    };
    const handleResume = async (leadId: string) => {
        const reqBody = {
            command: 'RESUME',
            leadId: leadId,
        };
        try {
            const res = await MakeAxiosRequest('POST', APIUrls.getURL('LEADS'), reqBody);
            if (res?.status === 200) {
                Toastify.success('Lead Resumed');
                fetchData();
            }
        } catch (error) {
            console.log(error);
        }
    };

    const handlePauseOrPlay = async (leadId: string) => {
        const lead = leads.find(it => it.id === leadId);

        if (lead?.status?.isPaused) {
            await handleResume(leadId);
            return;
        }

        if (lead?.status?.workflowId) {
            await handlePause(leadId);
            return;
        }

        await handleStart(leadId);
    };

    const fetchData = async () => {
        try {
            const res = await MakeAxiosRequest('GET', APIUrls.getURL('LEADS'));
            if (res?.status === 200) {
                setLeads(res?.data?.leads);
            }
        } catch (error) {
            console.log(error);
        } finally {
            setIsLoading(false);
        }
    };
    useEffect(() => {
        fetchData();
        const interval = setInterval(fetchData, 5000);
        return () => clearInterval(interval);
    }, []);

    return (
        <div className="w-full overflow-x-auto">
            <div className="mb-6 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                <h1 className="text-2xl font-bold text-gray-800">Leads</h1>
                <button onClick={() => setUploadLeadsModal(true)} disabled={isLoading} className="text-sm bg-gradient-purple hover:hover-gradient-purple disabled:bg-gray-400 disabled:cursor-not-allowed transition text-white py-3 px-6 rounded-xl font-semibold shadow-sm whitespace-nowrap flex items-center gap-2">
                    {isLoading && <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full" />}
                    {isLoading ? 'Loading...' : 'Upload Leads'}
                </button>
            </div>
            {isLoading && (
                <div className="flex justify-center items-center h-64">
                    <div className="animate-spin h-10 w-10 border-4 border-purple-500 border-t-transparent rounded-full"></div>
                </div>
            )}
            {!isLoading &&
                (leads && leads.length > 0 ? (
                    <div className="bg-white border-collapse border-[1px] border-gray-300 rounded-xl shadow-sm overflow-hidden">
                        <div className="overflow-x-auto">
                            <table className="min-w-max">
                                <thead className="border-b border-gray-200 bg-white">
                                    <tr>
                                        <th className="px-4 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider"> </th>
                                        <th className="px-4 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">Name</th>
                                        <th className="px-4 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">LinkedIn Url</th>
                                        <th className="px-4 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">Company</th>
                                        <th className="px-4 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">Title</th>
                                        <th className="px-4 py-4 text-xs font-semibold text-gray-700 uppercase tracking-wider">Actions</th>
                                        <th className="px-4 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">Created At</th>
                                        <th className="px-4 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider pr-6">Last Updated</th>
                                    </tr>
                                </thead>
                                <tbody className="bg-white divide-y divide-gray-200">
                                    {leads.map(lead => (
                                        <tr key={lead.id} className="hover:bg-gray-50 transition-colors">
                                            <td className="px-4 py-4">
                                                <div className="flex items-center">{lead.profile_image_url ? <img src={lead.profile_image_url} alt={lead.full_name || lead.id} className="h-10 w-10 rounded-full object-cover mr-3 flex-shrink-0" /> : <User2Icon />}</div>
                                            </td>
                                            <td>
                                                <div className="min-w-0 pl-2">
                                                    <div className="text-sm font-medium text-gray-900 truncate">{lead.full_name || (!lead.last_fetched_at ? 'Fetching...' : 'Unknown')}</div>
                                                    {lead.headline && <div className="text-sm text-gray-500 truncate max-w-[180px]">{lead.headline}</div>}
                                                </div>
                                            </td>
                                            <td className="px-4 py-4">
                                                <div className="truncate max-w-[180px]">
                                                    <a href={lead.linkedin_url} target="_blank" rel="noopener noreferrer" className="text-sm text-blue-600 hover:text-blue-800 hover:underline">
                                                        {lead.linkedin_url}
                                                    </a>
                                                </div>
                                            </td>
                                            <td className="px-4 py-4">
                                                <div className="text-sm text-gray-900 truncate max-w-[180px]">{lead.last_company_name || (!lead.last_fetched_at ? 'Fetching...' : 'N/A')}</div>
                                            </td>
                                            <td className="px-4 py-4">
                                                <div className="text-sm text-gray-900 truncate max-w-[180px]">{lead.last_job_title || (!lead.last_fetched_at ? 'Fetching...' : 'N/A')}</div>
                                            </td>
                                            <td className="px-4 py-4 flex flex-row gap-3 text-sm">
                                                <button onClick={() => handlePauseOrPlay(lead.id)} className="bg-gradient-purple hover:hover-gradient-purple transition text-white p-2 rounded-xl font-semibold shadow-sm whitespace-nowrap">
                                                    {lead?.status?.isPaused ? <PlayIcon size={20} /> : lead?.status?.workflowId ? <PauseIcon size={20} /> : <PlayIcon size={20} />}
                                                </button>
                                                <button onClick={() => setDeleteLeadId(lead.id)} className="bg-red-600 hover:bg-red-500 transition text-white p-2 rounded-xl font-semibold shadow-sm whitespace-nowrap">
                                                    <Trash2 size={20} />
                                                </button>
                                            </td>
                                            <td className="px-4 py-4 text-sm text-gray-500">{formatDate(lead.created_at)}</td>
                                            <td className="px-4 py-4 text-sm text-gray-500 pr-6">{formatDate(lead.updated_at)}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                ) : (
                    <div className="bg-white rounded-xl shadow-sm p-12 text-center">
                        <div className="text-gray-400 mb-4">
                            <svg className="mx-auto h-12 w-12" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                            </svg>
                        </div>
                        <h3 className="text-lg font-medium text-gray-900 mb-2">No Leads Uploaded</h3>
                        <p className="text-gray-500 mb-6">Get started by Uploading your leads.</p>
                        <button className="bg-gradient-purple hover:hover-gradient-purple transition text-white py-2 px-6 rounded-xl font-semibold shadow-sm" onClick={() => setUploadLeadsModal(true)}>
                            Upload Leads
                        </button>
                    </div>
                ))}

            {/* UPLOAD MODAL */}
            <Modal isOpen={uploadLeadsModal} onClose={() => setUploadLeadsModal(false)} onConfirm={handleSubmit} showFooter={true} title="Upload Leads" width="w-2/5" height="max-h-3/4 h-fit">
                <div className="flex flex-col gap-4">
                    <div className="flex gap-2">
                        <button onClick={() => csvFileInputRef.current?.click()} className="flex-1 flex items-center justify-center gap-2 py-2 px-4 border-2 border-dashed border-purple-300 rounded-xl text-purple-600 hover:bg-purple-50 transition">
                            <Upload size={18} />
                            <span className="text-sm font-medium">Upload CSV</span>
                        </button>
                        <input ref={csvFileInputRef} type="file" accept=".csv" onChange={handleCsvUpload} className="hidden" />
                    </div>
                    <div className="text-xs text-gray-500 bg-gray-50 p-2 rounded">
                        CSV must contain a <strong>linkedin_url</strong> column with valid LinkedIn profile URLs
                    </div>
                    <div className="flex justify-between items-center mb-2">
                        <span className="text-sm font-semibold text-gray-700">LinkedIn URLs</span>
                        <span className="text-sm text-gray-500">{linkedinUrls.length}/10 leads</span>
                    </div>
                    <div className="flex flex-col gap-2">
                        {linkedinUrls.map((it, idx) => (
                            <div key={idx} className="flex items-center gap-2">
                                <Input
                                    isErrored={!it.match(linkedinUrlRegex)}
                                    value={it}
                                    onChange={value => {
                                        const updatedUrls = [...linkedinUrls];
                                        updatedUrls[idx] = value;
                                        setLinkedinUrls(updatedUrls);
                                    }}
                                    placeholder="Enter LinkedIn URL"
                                />
                                {idx === linkedinUrls.length - 1 ? (
                                    <button disabled={linkedinUrls.length >= 10} onClick={() => linkedinUrls.length < 10 && setLinkedinUrls([...linkedinUrls, ''])} className="text-purple-500 hover:text-purple-600 disabled:text-gray-300 disabled:cursor-not-allowed transition p-2">
                                        <PlusIcon />
                                    </button>
                                ) : (
                                    <button onClick={() => setLinkedinUrls(linkedinUrls.filter((_, i) => i !== idx))} className="text-purple-500 hover:text-purple-600 transition p-2">
                                        <Minus />
                                    </button>
                                )}
                            </div>
                        ))}
                    </div>
                    <div className="border-t border-gray-200 pt-4">
                        <h3 className="text-sm font-semibold text-gray-700 mb-3">Lead Configuration</h3>
                        <div className="flex flex-col gap-3">
                            <Toggle checked={alertJobChange} onChange={setAlertJobChange} label="Alert on Job Change" />
                            <Toggle checked={alertPosts} onChange={setAlertPosts} label="Alert on Posts" />
                            <Toggle checked={alertExperienceChange} onChange={setAlertExperienceChange} label="Alert on Experience Change" />
                            <Toggle checked={alertTitleChange} onChange={setAlertTitleChange} label="Alert on Title Change" />
                            <Toggle checked={alertHiringOrFiring} onChange={setAlertHiringOrFiring} label="Alert on Hiring Or Firing" />
                            <Toggle checked={alertMerger} onChange={setAlertMerger} label="Alert on Merger" />
                            <Toggle checked={alertAquisition} onChange={setAlertAquisition} label="Alert on Aquisition" />
                            <Toggle checked={alertFunding} onChange={setAlertFunding} label="Alert on Funding" />
                        </div>
                    </div>
                </div>
            </Modal>
            <Modal isOpen={!!deleteLeadId} onClose={() => setDeleteLeadId(undefined)} onConfirm={() => handleDelete(deleteLeadId!)} title={`Are you sure you want to delete this lead?`}>
                {deleteLeadId &&
                    (() => {
                        const lead = leads.find(it => it.id === deleteLeadId);
                        return (
                            <div className="space-y-4">
                                <div className="bg-gray-50 p-4 rounded-lg">
                                    <p className="text-sm text-gray-600 mb-2">
                                        <strong>Name:</strong> {lead?.full_name || (!lead?.last_fetched_at ? 'Fetching...' : 'Unknown')}
                                    </p>
                                    <p className="text-sm text-gray-600 mb-2">
                                        <div className="truncate max-w-full">
                                            <strong>Linkedin: </strong>
                                            <a href={lead?.linkedin_url} target="_blank" rel="noopener noreferrer" className="text-sm text-blue-600 hover:text-blue-800 hover:underline">
                                                {lead?.linkedin_url}
                                            </a>
                                        </div>
                                    </p>
                                    <p className="text-sm text-gray-600">
                                        <strong>Company:</strong> {lead?.last_company_name || (!lead?.last_fetched_at ? 'Fetching...' : 'N/A')}
                                    </p>
                                </div>
                            </div>
                        );
                    })()}
            </Modal>
        </div>
    );
};

export default Leads;
